import Modal from "@/components/Modal";
import { ReactComponent as UserPlus } from "@/assets/svgs/user-plus.svg";
import { useForm } from "react-hook-form";
import PropTypes from "prop-types";
import { isAddress } from "ethers/lib/utils";
import useDid from "@/hooks/use-did/index";
import { validateEmail } from "@/utils/validate-email";

const AddNewAddressModal = ({
	visible,
	hide,
	newAddress,
	setNewAddress,
	availableAddresses,
}) => {

	const { toFindOrg } = useDid();
	const { register, handleSubmit, formState } = useForm({
		mode: "onChange",
	});
	const { errors } = formState;

	const onSubmit = (data) => {
		// Remove email if empty
		if (data.email === "") {
			delete data.email;
		}

		// TODO: Send data to API

		setNewAddress(data);
		hide();
	};

	function checkDuplicate(name, key) {
		const duplicate = availableAddresses?.find(
			(address) => address[key].toLowerCase() === name.toLowerCase()
		);
		return Boolean(duplicate);
	}

	return (
		<Modal visible={visible}>
			<div className="etr__record-details__modal-content">
				<div className="etr__record-details__modal-header">
					<div className="etr__record-details__section__icon">
						<UserPlus />
					</div>
					Add New Address
				</div>
				<form
					onSubmit={handleSubmit(onSubmit)}
					className="etr__record-details__modal__form"
				>
					<div className="etr__record-details__modal__form__input">
						<label
							htmlFor="name"
							className="etr__record-details__modal__form__input__label"
						>
							Organisation
						</label>
						<input
							id="name"
							ref={register({
								required: "This field is required",
								validate: (v) => {
									if (!v || v.trim() === "") {
										return "This field is required";
									}
									const duplicate = checkDuplicate(v, "name");
									if (duplicate) {
										return "This organisation already exists";
									}
									return true;
								},
							})}
							className="etr__record-details__modal__form__input__input"
							name="name"
							type="text"
							placeholder="Enter Text Here"
							defaultValue={newAddress?.name}
						/>
						{errors.name && (
							<span className="etr__record-details__modal__form__input__error">
								{errors.name.message}
							</span>
						)}
					</div>
					<div
						id="form-input"
						className="etr__record-details__modal__form__input"
					>
						<label
							htmlFor="walletAddress"
							className="etr__record-details__modal__form__input__label"
						>
							Wallet Address
						</label>
						<input
							id="walletAddress"
							ref={register({
								required: "This field is required",
								validate: (v) => {
									const validAddress = isAddress(v);
									if (!validAddress) {
										return "Invalid address format";
									}
									const duplicate = checkDuplicate(
										v,
										"walletAddress"
									);
									if (duplicate) {
										return "This wallet address already exists";
									}
									const isPitstopAddress = toFindOrg(v);
									if (isPitstopAddress) {
										return "You can only add a Pitstop wallet address via the Admin Portal. Please approach your administrator.";
									}
									return true;
								}
							})}
							className="etr__record-details__modal__form__input__input"
							name="walletAddress"
							type="text"
							placeholder="Enter Text Here"
							defaultValue={newAddress?.walletAddress}
						/>
						{errors.walletAddress && (
							<span className="etr__record-details__modal__form__input__error">
								{errors.walletAddress.message}
							</span>
						)}
					</div>
					<div
						id="form-input"
						className="etr__record-details__modal__form__input"
					>
						<label
							htmlFor="email"
							className="etr__record-details__modal__form__input__label"
						>
							Email
						</label>
						<input
							id="email"
							name="email"
							type="email"
							ref={register({
								required: "This field is required",
								validate: (v) => {
									const validEmail = validateEmail(v);
									if (!validEmail) {
										return "Invalid email format";
									}
									return true
								},
							})}
							className="etr__record-details__modal__form__input__input"
							placeholder="Enter Text Here"
							defaultValue={newAddress?.email}
						/>
						{errors.email && (
							<span className="etr__record-details__modal__form__input__error">
								{errors.email.message}
							</span>
						)}
					</div>
					<div className="etr__record-details__modal__footer">
						<button
							className="button button--rounded button--ghost"
							onClick={hide}
						>
							Cancel
						</button>
						<input
							className="button button--rounded"
							type="submit"
							value="Confirm"
							disabled={!formState.isValid}
						/>
					</div>
				</form>
			</div>
			<div
				className="etr__record-details__modal__overlay"
				onClick={hide}
				role="button"
				onKeyUp={(e) => {
					if (e.key === "Escape") {
						hide();
					}
				}}
				tabIndex="0"
				aria-label="Close modal"
			></div>
		</Modal>
	);
};

AddNewAddressModal.propTypes = {
	visible: PropTypes.bool.isRequired,
	hide: PropTypes.func.isRequired,
	newAddress: PropTypes.shape({
		name: PropTypes.string,
		walletAddress: PropTypes.string,
		email: PropTypes.string,
	}),
	setNewAddress: PropTypes.func.isRequired,
	availableAddresses: PropTypes.array,
};

export default AddNewAddressModal;
